// ============================================================
// Freddy Leads Analytics — GA4 + Meta Pixel
// ------------------------------------------------------------
// gtag is injected via /public/index.html (id G-H9TMD6KZK9)
// fbq  is injected via /public/index.html (id 881128764359225)
// Both are safe to call before they finish loading (queued).
//
// Conversion event names below must MATCH the names you mark
// as "Key events / Conversions" inside GA4 Admin → Events.
// See /docs/CONVERSIONS_SETUP.md for the dashboard walkthrough.
// ============================================================

const isBrowser = () => typeof window !== 'undefined';

// ---------- Low-level senders ----------
export const trackEvent = (eventName, params = {}) => {
  try {
    if (isBrowser() && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch (_e) { /* no-op */ }
};

// Meta Pixel — `kind` is 'track' for standard events, 'trackCustom' for custom.
export const trackPixel = (eventName, params = {}, kind = 'track') => {
  try {
    if (isBrowser() && typeof window.fbq === 'function' && !window._fbDisabled) {
      window.fbq(kind, eventName, params);
    }
  } catch (_e) { /* no-op */ }
};

// ---------- Existing helpers (kept for back-compat) ----------
export const trackFormSubmit = (formType, params = {}) =>
  trackEvent('form_submit', { form_type: formType, ...params });

export const trackSignUp = (role) =>
  trackEvent('sign_up', { method: 'email', role });

export const trackLogin = (role) =>
  trackEvent('login', { method: 'email', role });

// ============================================================
// CONVERSION HELPERS — fire BOTH GA4 + Meta Pixel
// Each helper sends a clearly-named GA4 event AND its closest
// standard Meta Pixel equivalent so that ad platforms can
// optimise on the same business outcome.
// ============================================================

// a) Brand signup completed → GA4 "brand_signup" / Meta "Lead"
export const trackBrandSignup = (params = {}) => {
  const payload = { event_category: 'conversion', role: 'brand', ...params };
  trackEvent('brand_signup', payload);
  trackEvent('sign_up', { method: 'email', role: 'brand', ...params });
  trackPixel('Lead', { content_category: 'brand_signup', ...params });
};

// b) Executive signup completion (after onboarding wizard) →
//    GA4 "exec_signup_completed" / Meta "CompleteRegistration"
export const trackExecOnboardingComplete = (params = {}) => {
  const payload = { event_category: 'conversion', role: 'executive', ...params };
  trackEvent('exec_signup_completed', payload);
  trackPixel('CompleteRegistration', {
    content_category: 'exec_onboarding',
    status: 'completed',
    ...params,
  });
};

// c) Campaign created by a brand → GA4 "campaign_created" / Meta "Purchase"
//    Pass `value` (budget in USD) and `currency` so ad platforms can value-optimise.
export const trackCampaignCreated = ({ value, currency = 'USD', campaign_id, offering_type, ...rest } = {}) => {
  const numericValue = Number(value) || 0;
  trackEvent('campaign_created', {
    event_category: 'conversion',
    value: numericValue,
    currency,
    campaign_id,
    offering_type,
    ...rest,
  });
  trackPixel('Purchase', {
    value: numericValue,
    currency,
    content_category: 'campaign',
    content_ids: campaign_id ? [campaign_id] : undefined,
    content_name: offering_type,
    ...rest,
  });
};

// d) Direct offer purchased / accepted by exec → GA4 "offer_purchased" / Meta "Purchase"
export const trackDirectOfferPurchased = ({ value, currency = 'USD', offer_id, campaign_id, ...rest } = {}) => {
  const numericValue = Number(value) || 0;
  trackEvent('offer_purchased', {
    event_category: 'conversion',
    value: numericValue,
    currency,
    offer_id,
    campaign_id,
    ...rest,
  });
  trackPixel('Purchase', {
    value: numericValue,
    currency,
    content_category: 'direct_offer',
    content_ids: offer_id ? [offer_id] : undefined,
    ...rest,
  });
};

// e) "Book a Demo" / Hubspot CTA click → GA4 "book_demo_click" / Meta "Schedule"
export const trackBookDemoClick = (params = {}) => {
  trackEvent('book_demo_click', { event_category: 'conversion', ...params });
  trackPixel('Schedule', { content_category: 'book_demo', ...params });
  // Also fire generic Lead for retargeting audiences
  trackPixel('Lead', { content_category: 'book_demo', ...params });
};

// ---------- Existing offer-status helpers ----------
export const trackOfferAccepted = (params = {}) => {
  trackEvent('offer_accepted', { event_category: 'conversion', ...params });
  trackPixel('Lead', { content_category: 'offer_accepted', ...params });
};

export const trackOfferDeclined = (params = {}) =>
  trackEvent('offer_declined', { event_category: 'conversion', ...params });

export const trackOfferCountered = (params = {}) =>
  trackEvent('offer_countered', { event_category: 'conversion', ...params });

export const trackPaymentMethodAdded = (params = {}) => {
  trackEvent('payment_method_added', { event_category: 'conversion', ...params });
  trackPixel('AddPaymentInfo', params);
};
